import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = { class: "message-composer__card" }
const _hoisted_3 = {
  key: 0,
  class: "message-composer__attachment-strip"
}
const _hoisted_4 = { class: "message-composer__editor-stack" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["aria-hidden"]
const _hoisted_7 = { class: "message-composer__toolbar" }
const _hoisted_8 = { class: "message-composer__toolbar-left" }
const _hoisted_9 = {
  key: 1,
  class: "message-composer__toolbar-divider",
  "aria-hidden": "true"
}
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["aria-label", "data-tippy-content", "disabled"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "message-composer__toolbar-right message-composer__actions" }
const _hoisted_14 = {
  key: 0,
  class: "message-composer__spinner",
  xmlns: "http://www.w3.org/2000/svg",
  width: "16",
  height: "16",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  "stroke-linecap": "round",
  "stroke-linejoin": "round"
}
const _hoisted_15 = {
  key: 1,
  class: "icon-up-arrow-16",
  "aria-hidden": "true"
}

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import Button from '@/components/ui/button/Button.vue';
import OverflowMenu from '@/components/ui/overflow-menu/OverflowMenu.vue';
import type { OverflowMenuOption } from '@/components/ui/overflow-menu/OverflowMenu';
import TextArea from '@/components/ui/text-area/TextArea.vue';

import type { MessageComposerExpose } from './MessageComposer';

type TextAreaExpose = {
  focus: () => void;
  setSelectionRange: (start: number, end: number) => void;
  textareaRef: HTMLTextAreaElement | null;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'MessageComposer',
  props: {
    modelValue: { default: '' },
    placeholder: { default: 'Add interaction notes' },
    disabled: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    sendDisabled: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    forceExpanded: { type: Boolean, default: false },
    hasAttachments: { type: Boolean, default: false },
    canAttach: { type: Boolean, default: true },
    avatarUrl: { default: '' },
    highlightHtml: { default: '' },
    showPlusMenu: { type: Boolean, default: true },
    showMention: { type: Boolean, default: true },
    showLink: { type: Boolean, default: true },
    showAttach: { type: Boolean, default: true },
    linkAriaLabel: { default: 'Link shipment reference' },
    linkTooltip: { default: 'Link' },
    plusMenuKey: { default: 0 },
    plusMenuOptions: { default: () => [{
    key: 'upload',
    label: 'Upload file',
    icon: 'upload_file'
  }] }
  },
  emits: ["update:modelValue", "submit", "cancel", "mention", "link", "attach", "upload", "input", "focus", "blur", "keydown", "selection-change", "plus-open"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const props = __props;

const emit = __emit;

const rootRef = ref<HTMLElement | null>(null);
const highlightRef = ref<HTMLElement | null>(null);
const textareaComponentRef = ref<TextAreaExpose | null>(null);
const isExpanded = ref(false);
let collapseTimeoutId: number | null = null;
let layoutFrame: number | null = null;
const layoutTimeouts: number[] = [];

const hasContent = computed(() => (
  props.modelValue.trim().length > 0 || props.hasAttachments
));

const showExpanded = computed(() => (
  isExpanded.value
  || props.forceExpanded
  || props.isEditing
  || hasContent.value
));

const isSendDisabled = computed(() => (
  props.sendDisabled
  || props.disabled
  || props.sending
  || (!hasContent.value && !props.isEditing)
));

const resolvedHighlightHtml = computed(() => {
  if (props.highlightHtml) {
    return props.highlightHtml;
  }

  if (!props.modelValue) {
    return '';
  }

  return formatPlainHtml(props.modelValue);
});

function formatPlainHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function getTextareaElement(): HTMLTextAreaElement | null {
  return textareaComponentRef.value?.textareaRef || null;
}

function clearCollapseTimeout(): void {
  if (collapseTimeoutId !== null) {
    window.clearTimeout(collapseTimeoutId);
    collapseTimeoutId = null;
  }
}

function clearScheduledLayout(): void {
  if (layoutFrame !== null) {
    window.cancelAnimationFrame(layoutFrame);
    layoutFrame = null;
  }

  while (layoutTimeouts.length > 0) {
    const timeoutId = layoutTimeouts.pop();
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

function syncOverlayHeight(): void {
  const textarea = getTextareaElement();
  if (!textarea || !highlightRef.value) {
    return;
  }

  highlightRef.value.style.height = `${textarea.offsetHeight}px`;
}

function syncScroll(): void {
  const textarea = getTextareaElement();
  if (!textarea || !highlightRef.value) {
    return;
  }

  highlightRef.value.scrollTop = textarea.scrollTop;
  highlightRef.value.scrollLeft = textarea.scrollLeft;
}

function syncTextareaHeight(): void {
  const textarea = getTextareaElement();
  if (!textarea) {
    return;
  }

  if (!showExpanded.value && !props.modelValue.trim()) {
    textarea.style.height = '';
    textarea.style.overflowY = 'hidden';
    syncOverlayHeight();
    syncScroll();
    return;
  }

  textarea.style.height = 'auto';
  const scrollHeight = textarea.scrollHeight;
  const nextHeight = Math.min(scrollHeight, 120);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = scrollHeight > 120 ? 'auto' : 'hidden';
  syncOverlayHeight();
  syncScroll();
}

function scheduleLayoutSync(): void {
  clearScheduledLayout();

  void nextTick(() => {
    syncTextareaHeight();

    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = null;
      syncTextareaHeight();
    });

    for (const delayMs of [80, 220]) {
      const timeoutId = window.setTimeout(() => {
        const index = layoutTimeouts.indexOf(timeoutId);
        if (index >= 0) {
          layoutTimeouts.splice(index, 1);
        }
        syncTextareaHeight();
      }, delayMs);
      layoutTimeouts.push(timeoutId);
    }
  });
}

function expand(): void {
  clearCollapseTimeout();
  isExpanded.value = true;
  void nextTick(() => {
    syncTextareaHeight();
  });
}

function collapse(): void {
  clearCollapseTimeout();
  isExpanded.value = false;

  const textarea = getTextareaElement();
  if (textarea) {
    textarea.style.height = '';
    textarea.style.overflowY = 'hidden';
  }

  syncOverlayHeight();
  syncScroll();
}

function tryCollapse(): void {
  if (props.isEditing || hasContent.value || props.forceExpanded || props.hasAttachments) {
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) {
    return;
  }

  collapse();
}

function scheduleCollapseCheck(): void {
  clearCollapseTimeout();
  collapseTimeoutId = window.setTimeout(() => {
    collapseTimeoutId = null;
    tryCollapse();
  }, 0);
}

function onModelUpdate(value: string): void {
  emit('update:modelValue', value);
}

function handleFocus(event: FocusEvent): void {
  expand();
  emit('focus', event);
}

function handleBlur(event: FocusEvent): void {
  scheduleCollapseCheck();
  emit('blur', event);
}

function handleInput(event: Event): void {
  syncTextareaHeight();
  emit('input', event);
}

function handleKeydown(event: KeyboardEvent): void {
  emit('keydown', event);
}

function handlePlusOpen(): void {
  expand();
  emit('plus-open');
}

function handlePlusSelect(actionKey: string): void {
  if (actionKey === 'upload') {
    emit('upload');
  }
}

function focus(): void {
  textareaComponentRef.value?.focus();
}

function setSelectionRange(start: number, end: number): void {
  textareaComponentRef.value?.setSelectionRange(start, end);
}

watch(hasContent, (nextHasContent) => {
  if (nextHasContent) {
    expand();
  }
});

watch(() => props.forceExpanded, (forced) => {
  if (forced) {
    expand();
  } else {
    scheduleCollapseCheck();
  }
});

watch(() => props.modelValue, () => {
  void nextTick(() => {
    syncTextareaHeight();
  });
});

onBeforeUnmount(() => {
  clearCollapseTimeout();
  clearScheduledLayout();
});

__expose<MessageComposerExpose>({
  focus,
  setSelectionRange,
  getTextareaElement,
  getRootElement: () => rootRef.value,
  expand,
  collapse,
  syncLayout: scheduleLayoutSync
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: _normalizeClass(["message-composer", {
    'is-expanded': showExpanded.value,
    editing: __props.isEditing
  }])
  }, [
    (__props.avatarUrl)
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          class: "message-composer__avatar-sr",
          src: __props.avatarUrl,
          alt: ""
        }, null, 8 /* PROPS */, _hoisted_1))
      : _createCommentVNode("v-if", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass(['message-composer__input-shell', { 'is-empty': !__props.modelValue }])
      }, [
        (__props.hasAttachments)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _renderSlot(_ctx.$slots, "attachments")
            ]))
          : _createCommentVNode("v-if", true),
        _createElementVNode("div", _hoisted_4, [
          (!__props.modelValue)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass(["message-composer__placeholder-guide", { 'is-collapsed': !showExpanded.value }]),
                "aria-hidden": "true"
              }, _toDisplayString(__props.placeholder), 3 /* TEXT, CLASS */))
            : _createCommentVNode("v-if", true),
          _createElementVNode("div", {
            ref_key: "highlightRef",
            ref: highlightRef,
            class: "message-composer__highlight",
            "aria-hidden": "true",
            innerHTML: resolvedHighlightHtml.value
          }, null, 8 /* PROPS */, _hoisted_5),
          _createVNode(TextArea, {
            ref_key: "textareaComponentRef",
            ref: textareaComponentRef,
            "model-value": __props.modelValue,
            class: "message-composer__textarea-field",
            "textarea-class": 'message-composer__textarea',
            placeholder: __props.placeholder,
            rows: 1,
            "auto-resize": false,
            disabled: __props.disabled,
            "onUpdate:modelValue": onModelUpdate,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onInput: handleInput,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('selection-change', $event))),
            onKeydown: handleKeydown,
            onKeyup: _cache[1] || (_cache[1] = ($event: any) => (emit('selection-change', $event))),
            onScroll: syncScroll
          }, null, 8 /* PROPS */, ["model-value", "placeholder", "disabled"])
        ])
      ], 2 /* CLASS */),
      _createElementVNode("div", {
        class: "message-composer__toolbar-shell",
        "aria-hidden": showExpanded.value ? undefined : 'true'
      }, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            (__props.showPlusMenu && !__props.isEditing)
              ? (_openBlock(), _createBlock(OverflowMenu, {
                  key: __props.plusMenuKey,
                  class: "message-composer__plus-menu",
                  align: "left",
                  "button-label": "Add message options",
                  "trigger-tooltip": "Add",
                  disabled: !__props.canAttach,
                  portal: "",
                  placement: "top",
                  "trigger-class": { 'message-composer__action': true, 'plus': true, 'message-composer__toolbar-trigger': true, disabled: !__props.canAttach },
                  options: __props.plusMenuOptions,
                  onOpen: handlePlusOpen,
                  onSelect: handlePlusSelect
                }, {
                  trigger: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createElementVNode("span", {
                      class: "icon-plus-18",
                      "aria-hidden": "true"
                    }, null, -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled", "trigger-class", "options"]))
              : _createCommentVNode("v-if", true),
            (__props.showPlusMenu && !__props.isEditing)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9))
              : _createCommentVNode("v-if", true),
            (__props.showMention)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  type: "button",
                  class: "message-composer__toolbar-trigger",
                  "aria-label": "Mention someone",
                  "data-tippy-content": "Mention",
                  "data-tippy-placement": "top",
                  "data-tippy-delay": "2000,0",
                  disabled: __props.disabled || __props.sending,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('mention')))
                }, [...(_cache[9] || (_cache[9] = [
                  _createElementVNode("span", {
                    class: "icon-at-18",
                    "aria-hidden": "true"
                  }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_10))
              : _createCommentVNode("v-if", true),
            (__props.showLink)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 3,
                  type: "button",
                  class: "message-composer__toolbar-trigger",
                  "aria-label": __props.linkAriaLabel,
                  "data-tippy-content": __props.linkTooltip,
                  "data-tippy-placement": "top",
                  "data-tippy-delay": "2000,0",
                  disabled: __props.disabled || __props.sending,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('link')))
                }, [...(_cache[10] || (_cache[10] = [
                  _createElementVNode("span", {
                    class: "icon-link-18",
                    "aria-hidden": "true"
                  }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_11))
              : _createCommentVNode("v-if", true),
            (__props.showAttach)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 4,
                  type: "button",
                  class: "message-composer__toolbar-trigger",
                  "aria-label": "Attach file",
                  "data-tippy-content": "Attach",
                  "data-tippy-placement": "top",
                  "data-tippy-delay": "2000,0",
                  disabled: !__props.canAttach,
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('attach')))
                }, [...(_cache[11] || (_cache[11] = [
                  _createElementVNode("span", {
                    class: "icon-paperclip-18",
                    "aria-hidden": "true"
                  }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_12))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_13, [
            (__props.isEditing)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(Button, {
                    type: "button",
                    class: "message-composer__action cancel",
                    variant: "ghost",
                    tone: "neutral",
                    "icon-only": "",
                    disabled: __props.disabled || __props.sending,
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (emit('cancel')))
                  }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createElementVNode("i", { class: "material-icons" }, "close", -1 /* CACHED */)
                    ]))]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["disabled"]),
                  _createVNode(Button, {
                    type: "button",
                    class: _normalizeClass(['message-composer__action', 'accept', { disabled: isSendDisabled.value }]),
                    variant: "soft",
                    tone: "primary",
                    "icon-only": "",
                    disabled: isSendDisabled.value,
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (emit('submit')))
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createElementVNode("i", { class: "material-icons" }, "check", -1 /* CACHED */)
                    ]))]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["class", "disabled"])
                ], 64 /* STABLE_FRAGMENT */))
              : (_openBlock(), _createBlock(Button, {
                  key: 1,
                  type: "button",
                  class: _normalizeClass(['message-composer__action', 'send', { disabled: isSendDisabled.value, sending: __props.sending }]),
                  variant: "soft",
                  tone: "neutral",
                  "icon-only": "",
                  disabled: isSendDisabled.value,
                  "aria-busy": __props.sending ? 'true' : 'false',
                  onClick: _cache[7] || (_cache[7] = ($event: any) => (emit('submit')))
                }, {
                  default: _withCtx(() => [
                    (__props.sending)
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_14, [...(_cache[14] || (_cache[14] = [
                          _createElementVNode("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }, null, -1 /* CACHED */)
                        ]))]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_15))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["class", "disabled", "aria-busy"]))
          ])
        ])
      ], 8 /* PROPS */, _hoisted_6)
    ]),
    _renderSlot(_ctx.$slots, "suggestions")
  ], 2 /* CLASS */))
}
}

})